"use client";
import * as LoginInterfaces from "@/types/login.types";
import * as UserTypes from "@/types/user.types";
import { isTvEnv } from "@/utils/common.helpers";
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import { ChangeEvent } from "react";

import { SendOTPButton } from "./SendOTPButton";
import { useSendOTP } from "@/hooks/useSendOTP";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useCountryList } from "@/hooks/useCountryList";
import { CountryCodePicker } from "./CountryCodePicker";
import {
  DEFAULT_COUNTRY,
  INDIA_PHONE_NUMBER_LENGTH,
  MAX_MOBILE_NUMBER_LENGTH,
  MIN_MOBILE_NUMBER_LENGTH,
} from "@/constants/constants";

export const SendOTP = ({
  sendOTPState,
  sendOTPFormAction,
  toggleLoginPageState,
  onInputFocus,
  onInputBlur,
  logTitle,
  logSubTitle,
}: {
  sendOTPState: UserTypes.SendOTPStateType;
  sendOTPFormAction: LoginInterfaces.SendOTPFormActionInterface;
  toggleLoginPageState: () => void;
  onInputFocus: () => void;
  onInputBlur: () => void;
  logTitle?: string;
  logSubTitle?: string;
}) => {
  const { t } = useClientTranslations();
  const { countries, selectedCountry, setSelectedCountry } = useCountryList();

  const maxDigits =
    selectedCountry.country === DEFAULT_COUNTRY
      ? INDIA_PHONE_NUMBER_LENGTH
      : 15;

  const {
    mobileNumber,
    errorMessage,
    setErrorMessage,
    isValidMobileNumber,
    validateAndSetMobileNumber,
    showValidationError,
    handleInputFocus: handleInputFocusFromHook,
    handleSubmit,
  } = useSendOTP({
    sendOTPState,
    maxDigits,
    onMobileNumberComplete: () => {
      setFocusOnSendOtpButton();
    },
  });

  const handleMobileNumberChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const value: string = e.target.value;
    if (isValidMobileNumber(value)) {
      setFocusOnSendOtpButton();
    }
    validateAndSetMobileNumber(value);
  };

  const { ref: mobileInputRef } = useFocusable({
    focusKey: "mobile-input",
    onArrowPress: (direction) => {
      if (direction === "down") {
        setFocusOnSendOtpButton();
        return false;
      }

      return true;
    },
  });

  const { ref: focusProviderRef, focusKey } = useFocusable({
    focusKey: "send-otp",
    focusable: false,
  });

  const setFocusOnSendOtpButton = () => {
    if (!isTvEnv()) {
      return;
    }
    mobileInputRef.current?.blur();
  };

  const handleInputFocus = () => {
    onInputFocus();
    handleInputFocusFromHook();
  };

  const handleInputBlur = () => {
    if (!isValidMobileNumber(mobileNumber)) {
      setErrorMessage(t("auth_screen.phone_number_error_msg"));
    } else {
      setErrorMessage("");
    }
    onInputBlur();
  };

  const handleMobileInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Enter") {
      if (isValidMobileNumber(mobileNumber)) {
        const form = e.currentTarget.closest("form");
        if (form) {
          form.requestSubmit();
          toggleLoginPageState();
        }
      } else {
        e.preventDefault();
        showValidationError();
      }
    }
  };

  return (
    <FocusContext.Provider value={focusKey}>
      <form
        ref={focusProviderRef}
        action={sendOTPFormAction}
        noValidate
        onSubmit={handleSubmit}
        className="relative w-[100%] max-w-[23rem] flex-col items-center justify-center"
      >
        <input
          type="hidden"
          name="countryCode"
          value={selectedCountry.countryCode}
        />
        <input type="hidden" name="country" value={selectedCountry.country} />
        <div className="flex flex-col items-center justify-center pt-5">
          <h2 className="mb-2 text-2xl font-medium md:text-2xl">
            {logTitle ?? t("auth_screen.loginTitle")}
          </h2>
          <p className="text-xs text-stg-grey-4">
            {logSubTitle ?? t("auth_screen.logsubTitle")}
          </p>
        </div>
        <div className="ios-input mb-2 flex flex-col justify-center gap-2 p-4 md:p-4">
          <div className="login-input-container relative z-[1] flex h-[52px] w-[100%] items-center justify-center rounded-lg border border-white/50 bg-stg-screen-background px-2 py-2 text-xs font-medium text-stg-grey-4">
            <div className="mr-4 flex h-[100%] items-center justify-center border-r border-white/50 pl-2 pr-2">
              <CountryCodePicker
                countries={countries}
                selectedCountry={selectedCountry}
                onSelect={setSelectedCountry}
              />
            </div>
            <label htmlFor="mobileNumber" className="hidden">
              {t("auth_screen.mobile_number")}
            </label>
            <input
              ref={mobileInputRef}
              type="tel"
              inputMode="numeric"
              placeholder={t("auth_screen.mobile_number")}
              id="mobileNumber"
              name="mobileNumber"
              className="h-[100%] w-[100%] bg-stg-screen-background text-base font-bold text-white caret-white outline-none focus:placeholder:text-stg-grey-3"
              value={mobileNumber}
              onChange={handleMobileNumberChange}
              onFocus={handleInputFocus}
              pattern={
                maxDigits === INDIA_PHONE_NUMBER_LENGTH
                  ? "[0-9]{10}"
                  : `[0-9]{${MIN_MOBILE_NUMBER_LENGTH},${MAX_MOBILE_NUMBER_LENGTH}}`
              }
              title="Please enter a valid Mobile Number"
              required
              onBlur={handleInputBlur}
              aria-invalid={Boolean(errorMessage)}
              aria-describedby="mobile-error"
              onKeyDown={handleMobileInputKeyDown}
            />
          </div>
          {errorMessage && (
            <div id="mobile-error" className="text-sm text-red-600">
              {errorMessage}
            </div>
          )}
          <SendOTPButton
            toggleLoginPageState={toggleLoginPageState}
            isValidPhoneNumber={isValidMobileNumber(mobileNumber)}
            onInvalid={showValidationError}
            isFocusAble={isTvEnv()}
          />
        </div>

        {sendOTPState?.errorMessage && (
          <div className="mx-auto mt-2 rounded bg-stg-red-1-50 px-8 text-center align-middle text-xs font-bold text-white backdrop-blur-sm">
            {sendOTPState.errorMessage}
          </div>
        )}
      </form>
    </FocusContext.Provider>
  );
};
